import { Component, OnInit, OnDestroy, OnChanges, SimpleChanges, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DragDropModule, CdkDragDrop, moveItemInArray, transferArrayItem, copyArrayItem } from '@angular/cdk/drag-drop';
import { FormFieldConfig, FormStage } from '../dynamic-form/dynamic-form/dynamic-form.component';
import { debounceTime, Subject } from 'rxjs';
import { ApprovalService } from '../items/approval/services/approval.service';
import { ApprovalFlowDefinition } from '../items/approval/models/approval-flow-definition.model';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { I18nService } from '../../services/i18n.service';
import { Router } from '@angular/router';
import { environment } from '../../services/global-environment';
import { FileUploadService } from '../../services/file-upload.service';
import { StageEditorComponent } from '../stage-editor/stage-editor.component';

// 元件分類定義
interface ComponentCategory {
  id: string;
  label: string;
  icon: string;
  expanded: boolean;
}

// 可用的表單元件定義
interface FormComponentDefinition {
  type: string;
  label: string;
  icon: string;
  category: string;
  defaultConfig: Partial<FormFieldConfig>;
}

// 正在建構的表單元件
interface FormBuilderItem extends FormFieldConfig {
  id: string;
  selected?: boolean;
  _unassigned?: boolean; // 用於標記未分配階段的元件
  _stageOrder?: number; // 用於標記元件所屬階段的順序
}

@Component({
  selector: 'app-form-builder',
  standalone: true,
  imports: [CommonModule, FormsModule, DragDropModule, TranslatePipe, StageEditorComponent],
  templateUrl: './form-builder.component.html',
  styleUrls: ['./form-builder.component.scss']
})

export class FormBuilderComponent implements OnInit, OnDestroy, OnChanges {
  @Input() initialConfig: FormFieldConfig[] = [];
  @Input() formName: string = ''; // 表單名稱，用於保存和載入配置
  @Output() configChange = new EventEmitter<FormFieldConfig[]>();
  @Output() previewChange = new EventEmitter<FormFieldConfig[]>();

  // 新增：支援簽核步驟模式的參數
  @Input() mode: 'full' | 'approval-step' = 'full'; // 運作模式
  @Input() allowedComponents: string[] = []; // 允許的元件類型
  @Input() simplifiedUI: boolean = false; // 是否使用簡化介面
  @Input() hideAdvancedFeatures: boolean = false; // 隱藏進階功能（如階段管理）
  @Input() formItems: FormBuilderItem[] = []; // 支援外部傳入表單項目
  @Output() formItemsChange = new EventEmitter<FormBuilderItem[]>(); // 表單項目變更事件

  // 階段化表單相關屬性
  @Output()  enableStageMode: boolean = false; // 是否啟用階段化表單模式

  // 可用的表單元件庫
  availableComponents: FormComponentDefinition[] = [];

  // 分類配置
  categories: ComponentCategory[] = [];

  // 搜尋相關
  searchTerm: string = '';
  filteredComponents: FormComponentDefinition[] = [];

  // 建構中的表單元件 - 移到 @Input 定義
  // formItems: FormBuilderItem[] = [];

  // 選中的元件
  selectedItem: FormBuilderItem | null = null;

  // 防抖更新主題
  private updateSubject = new Subject<void>();

  // 計數器用於生成唯一ID
  private idCounter = 1;

  // 簽核流程相關屬性
  availableApprovalFlows: ApprovalFlowDefinition[] = [];


  formStages: FormStage[] = [];
  currentEditingStage: string | null = null;
  editingStage: FormStage | null = null;
  isFirstTimeStageUser: boolean = true;
  showStageWizard: boolean = false;
  showStageEditor: boolean = false;

  // 面板收合狀態
  panelCollapsedState = {
    stageManagement: false,
    componentLibrary: false,
    formConstruction: false,
    properties: false
  };

  constructor(
    private approvalService: ApprovalService,
    private i18nService: I18nService,
    private router: Router,
    private fileUploadService: FileUploadService
  ) {}

  ngOnInit(): void {
    // 初始化分類配置
    this.initializeCategories();

    // 初始化可用元件庫
    this.initializeAvailableComponents();

    // 如果有初始配置，轉換為建構項目
    this.loadInitialConfig();

    // 載入簽核流程定義
    this.loadApprovalFlows();

    // 載入階段配置
    this.loadStageConfiguration();

    // 設置防抖更新
    this.updateSubject.pipe(debounceTime(150)).subscribe(() => {
      this.performUpdate();
    });

    // 初始化過濾的元件列表
    this.updateFilteredComponents();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 initialConfig 發生變化時，重新載入配置
    if (changes['initialConfig'] && !changes['initialConfig'].firstChange) {
      this.loadInitialConfig();
    }

    // 當模式或允許的元件發生變化時，更新過濾的元件列表
    if (changes['mode'] || changes['allowedComponents']) {
      this.updateFilteredComponents();
    }

    // 當外部傳入的 formItems 變化時，同步內部狀態
    if (changes['formItems'] && !changes['formItems'].firstChange) {
      // 確保每個項目都有唯一的 ID
      this.formItems.forEach((item, index) => {
        if (!item.id) {
          item.id = 'item' + (this.idCounter++);
        }
      });
    }
  }

  ngOnDestroy(): void {
    // 清理資源，如果有訂閱的話可以在這裡取消訂閱
    this.updateSubject.complete();
  }

  // 初始化分類配置
  private initializeCategories(): void {
    this.categories = [
      {
        id: 'input',
        label: this.i18nService.translate('form.builder.category.input'),
        icon: 'fas fa-edit',
        expanded: true // 預設展開最常用的分類
      },
      {
        id: 'selection',
        label: this.i18nService.translate('form.builder.category.selection'),
        icon: 'fas fa-check-circle',
        expanded: true
      },
      {
        id: 'interactive',
        label: this.i18nService.translate('form.builder.category.interactive'),
        icon: 'fas fa-hand-pointer',
        expanded: false
      },
      {
        id: 'display',
        label: this.i18nService.translate('form.builder.category.display'),
        icon: 'fas fa-eye',
        expanded: false
      },
      {
        id: 'advanced',
        label: this.i18nService.translate('form.builder.category.advanced'),
        icon: 'fas fa-cogs',
        expanded: false
      }
    ];
  }

  // 初始化可用的表單元件庫
  private initializeAvailableComponents(): void {
    this.availableComponents = [
      {
        type: 'text-input',
        label: this.i18nService.translate('form.builder.text_input'),
        icon: 'fas fa-font',
        category: 'input',
        defaultConfig: {
          type: 'text-input',
          label: this.i18nService.translate('form.builder.text_input_field'),
          placeholder: this.i18nService.translate('form.builder.enter_text')
        }
      },
      {
        type: 'number-input',
        label: this.i18nService.translate('form.builder.number_input'),
        icon: 'fas fa-hashtag',
        category: 'input',
        defaultConfig: {
          type: 'number-input',
          label: this.i18nService.translate('form.builder.number_input_field'),
          placeholder: this.i18nService.translate('form.builder.enter_number')
        }
      },
      {
        type: 'textarea',
        label: this.i18nService.translate('form.builder.textarea'),
        icon: 'fas fa-align-left',
        category: 'input',
        defaultConfig: {
          type: 'textarea',
          label: this.i18nService.translate('form.builder.textarea_field'),
          placeholder: this.i18nService.translate('form.builder.enter_description')
        }
      },
      {
        type: 'fill-in',
        label: '填空元件',
        icon: 'fas fa-edit',
        category: 'input',
        defaultConfig: {
          type: 'fill-in',
          label: '填空欄位',
          template: '危險物報告 [weight] 公斤處理方式（[method]）',
          fields: [
            { id: 'weight', type: 'number', width: 'sm', placeholder: '重量' },
            { id: 'method', type: 'text', width: 'lg', placeholder: '處理方式' }
          ]
        }
      },
      {
        type: 'date',
        label: this.i18nService.translate('form.builder.date'),
        icon: 'fas fa-calendar-alt',
        category: 'input',
        defaultConfig: {
          type: 'date',
          label: this.i18nService.translate('form.builder.date_field'),
          placeholder: this.i18nService.translate('form.builder.please_select')
        }
      },
      {
        type: 'datetime',
        label: this.i18nService.translate('form.builder.datetime_input'),
        icon: 'fas fa-clock',
        category: 'input',
        defaultConfig: {
          type: 'datetime',
          label: this.i18nService.translate('form.builder.datetime_field'),
          placeholder: this.i18nService.translate('form.builder.please_select')
        }
      },
      {
        type: 'select',
        label: this.i18nService.translate('form.builder.select'),
        icon: 'fas fa-list',
        category: 'selection',
        defaultConfig: {
          type: 'select',
          label: this.i18nService.translate('form.builder.select_field'),
          placeholder: this.i18nService.translate('form.builder.please_select'),
          options: [
            { label: this.i18nService.translate('form.builder.option_name') + ' 1', value: 'option1' },
            { label: this.i18nService.translate('form.builder.option_name') + ' 2', value: 'option2' }
          ]
        }
      },
      {
        type: 'radiobutton',
        label: this.i18nService.translate('form.builder.radiobutton'),
        icon: 'fas fa-dot-circle',
        category: 'selection',
        defaultConfig: {
          type: 'radiobutton',
          label: this.i18nService.translate('form.builder.radiobutton_field'),
          options: [
            { label: this.i18nService.translate('form.builder.option_name') + ' 1', value: 'option1' },
            { label: this.i18nService.translate('form.builder.option_name') + ' 2', value: 'option2' }
          ]
        }
      },
      {
        type: 'multi-checkbox',
        label: this.i18nService.translate('form.builder.multi_checkbox'),
        icon: 'fas fa-check-square',
        category: 'selection',
        defaultConfig: {
          type: 'multi-checkbox',
          label: this.i18nService.translate('form.builder.checkbox_field'),
          options: [
            { label: this.i18nService.translate('form.builder.option_name') + ' 1', value: 'option1', selected: false },
            { label: this.i18nService.translate('form.builder.option_name') + ' 2', value: 'option2', selected: false }
          ]
        }
      },
      {
        type: 'department-selector',
        label: this.i18nService.translate('form.builder.department_selector'),
        icon: 'fas fa-sitemap',
        category: 'selection',
        defaultConfig: {
          type: 'department-selector',
          label: this.i18nService.translate('form.builder.department_field'),
          placeholder: this.i18nService.translate('form.builder.select_department')
        }
      },
      {
        type: 'personnel-selector',
        label: this.i18nService.translate('form.builder.personnel_selector'),
        icon: 'fas fa-users',
        category: 'selection',
        defaultConfig: {
          type: 'personnel-selector',
          label: this.i18nService.translate('form.builder.personnel_field'),
          placeholder: this.i18nService.translate('form.builder.select_personnel'),
          multiSelect: false
        }
      },
      {
        type: 'tags',
        label: this.i18nService.translate('form.builder.tags'),
        icon: 'fas fa-tags',
        category: 'selection',
        defaultConfig: {
          type: 'tags',
          label: this.i18nService.translate('form.builder.tags_field'),
          placeholder: this.i18nService.translate('form.builder.select_or_enter_tags'),
          options: [this.i18nService.translate('form.builder.tag_name') + ' 1', this.i18nService.translate('form.builder.tag_name') + ' 2']
        }
      },
      {
        type: 'location',
        label: this.i18nService.translate('form.builder.location'),
        icon: 'fas fa-map-marker-alt',
        category: 'interactive',
        defaultConfig: {
          type: 'location',
          label: this.i18nService.translate('form.builder.location_field'),
          placeholder: this.i18nService.translate('form.builder.please_select')
        }
      },
      {
        type: 'photo-upload',
        label: this.i18nService.translate('form.builder.photo_upload'),
        icon: 'fas fa-camera',
        category: 'interactive',
        defaultConfig: {
          type: 'photo-upload',
          label: this.i18nService.translate('form.builder.photo_upload_field')
        }
      },
      {
        type: 'avatar-upload',
        label: this.i18nService.translate('form.builder.avatar_upload'),
        icon: 'fas fa-user-circle',
        category: 'interactive',
        defaultConfig: {
          type: 'avatar-upload',
          label: this.i18nService.translate('form.builder.avatar_upload_field'),
          placeholder: this.i18nService.translate('form.builder.upload_avatar'),
          required: false
        }
      },
      {
        type: 'signature',
        label: this.i18nService.translate('form.builder.signature'),
        icon: 'fas fa-signature',
        category: 'interactive',
        defaultConfig: {
          type: 'signature',
          label: this.i18nService.translate('form.builder.signature_field')
        }
      },
      {
        type: 'paragraph',
        label: this.i18nService.translate('form.builder.paragraph'),
        icon: 'fas fa-paragraph',
        category: 'display',
        defaultConfig: {
          type: 'paragraph',
          label: this.i18nService.translate('form.builder.paragraph_title'),
          content: this.i18nService.translate('form.builder.paragraph_description'),
          textAlign: 'left'
        }
      },
      {
        type: 'rich-text-display',
        label: '富文本顯示',
        icon: 'fas fa-code',
        category: 'display',
        defaultConfig: {
          type: 'rich-text-display',
          label: '富文本顯示',
          content: '<p><strong>歡迎使用富文本顯示元件！</strong></p><p>這個元件支援：</p><ul><li>HTML 標籤</li><li><em>文字樣式</em></li><li><a href="#">超連結</a></li><li>圖片嵌入</li></ul>',
          fontSize: '14px',
          color: '#333333',
          textAlign: 'left',
          lineHeight: '1.5',
          maxWidth: '100%'
        }
      },
      {
        type: 'timestamp',
        label: this.i18nService.translate('form.builder.timestamp'),
        icon: 'fas fa-clock',
        category: 'display',
        defaultConfig: {
          type: 'timestamp',
          label: this.i18nService.translate('form.builder.timestamp_field'),
          format: 'datetime'
        }
      },
      {
        type: 'approval-flow',
        label: this.i18nService.translate('form.builder.approval_flow'),
        icon: 'fas fa-route',
        category: 'advanced',
        defaultConfig: {
          type: 'approval-flow',
          label: this.i18nService.translate('form.builder.approval_flow'),
          placeholder: this.i18nService.translate('form.builder.please_select_approval_flow')
        }
      },
      {
        type: 'dynamic-api-select',
        label: '動態API下拉選單',
        icon: 'fas fa-database',
        category: 'advanced',
        defaultConfig: {
          type: 'dynamic-api-select',
          label: '動態API下拉選單',
          placeholder: '請選擇',
          apiKey: ''
        }
      },
      {
        type: 'dynamic-api-table',
        label: '動態API表格選擇',
        icon: 'fas fa-table',
        category: 'advanced',
        defaultConfig: {
          type: 'dynamic-api-table',
          label: '動態API表格選擇',
          placeholder: '請選擇項目',
          apiKey: '',
          maxSelections: 0 // 0 表示無限制
        }
      },
      {
        type: 'file-display',
        label: this.i18nService.translate('form.builder.file_display'),
        icon: 'fas fa-file-alt',
        category: 'display',
        defaultConfig: {
          type: 'file-display',
          label: this.i18nService.translate('form.builder.file_display_field')
        }
      },
      {
        type: 'file-upload-manager',
        label: this.i18nService.translate('form.builder.file_upload_manager'),
        icon: 'fas fa-cloud-upload-alt',
        category: 'input',
        defaultConfig: {
          type: 'file-upload-manager',
          label: this.i18nService.translate('form.builder.file_upload_manager_field'),
          fileUploadConfig: {
            maxFiles: 5,
            maxFileSize: 10,
            allowedTypes: ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'],
            enableDragDrop: true,
            enablePreview: true,
            allowRename: true,
            showFileSize: true,
            showUploadTime: true
          }
        }
      },
      {
        type: 'required-document-upload',
        label: this.i18nService.translate('form.builder.required_document_upload'),
        icon: 'fas fa-certificate',
        category: 'input',
        defaultConfig: {
          type: 'required-document-upload',
          label: this.i18nService.translate('form.builder.required_document_upload_field'),
          requiredDocumentConfig: {
            documentName: this.i18nService.translate('form.builder.default_document_name'),
            documentDescription: '',
            aiParsing: false,
            expiryDateField: '',
            acceptedFormats: ['.pdf', '.jpg', '.jpeg', '.png'],
            maxFileSize: 10,
            uploadHint: '',
            showFileSize: true,
            showUploadTime: true,
            enableDragDrop: true,
            enablePreview: true
          }
        }
      },
       {
        type: 'audio-recorder',
        label: this.i18nService.translate('form.builder.audio_recorder'),
        icon: 'fas fa-microphone',
        category: 'input',
        defaultConfig: {
          type: 'audio-recorder',
          label: this.i18nService.translate('form.builder.audio_recorder_field'),
          placeholder: this.i18nService.translate('form.builder.audio_recorder_placeholder'),
          maxDuration: 300, // 5分鐘
          minDuration: 1, // 1秒
          audioQuality: 'medium',
          audioFormat: 'wav',
          required: false
        }
      },
      {
        type: 'meeting-signature-collection',
        label: this.i18nService.translate('form.builder.multi_signature'),
        icon: 'fas fa-users',
        category: 'interactive',
        defaultConfig: {
          type: 'meeting-signature-collection',
          label: this.i18nService.translate('form.builder.multi_signature_field'),
          title: this.i18nService.translate('form.builder.multi_signature_title'),
          documentId: '',
          participants: []
        }
      },
      {
        type: 'video-player',
        label: this.i18nService.translate('form.builder.video_player'),
        icon: 'fas fa-play',
        category: 'display',
        defaultConfig: {
          type: 'video-player',
          label: this.i18nService.translate('form.builder.video_player_field'),
          youtubeUrl: '',
          videoTitle: '',
          videoDescription: '',
          videoWidth: '100%',
          videoHeight: '315px',
          videoAutoplay: false,
          videoControls: true,
          videoAllowFullscreen: true,
      }
      },
      {
        type: 'subform-relation',
        label: this.i18nService.translate('form.builder.subform_relation'),
        icon: 'fas fa-link',
        category: 'advanced',
        defaultConfig: {
          type: 'subform-relation',
          label: this.i18nService.translate('form.builder.subform_relation_field'),
          placeholder: '',
          formId: '',
          required: false
        }
      },
      {
        type: 'auto-numbering',
        label: this.i18nService.translate('form.builder.auto_numbering'),
        icon: 'fas fa-list-ol',
        category: 'input',
        defaultConfig: {
          type: 'auto-numbering',
          label: this.i18nService.translate('form.builder.auto_numbering_field'),
          placeholder: this.i18nService.translate('form.builder.auto_numbering_placeholder'),
          autoNumbering: {
            elements: [
              {
                type: 'text',
                order: 1,
                config: { text: 'DOC' }
              },
              {
                type: 'year',
                order: 2,
                config: { format: 'YYYY' }
              },
              {
                type: 'sequence',
                order: 3,
                config: { digits: 3, startFrom: 1 }
              }
            ],
            separator: '-'
          }
        }
      }
    ];
  }

  // 取得可用的元件列表（根據模式過濾）
  get availableComponentsFiltered(): FormComponentDefinition[] {
    if (this.mode === 'approval-step') {
      // 簽核步驟模式：只顯示允許的元件類型
      const allowedTypes = this.allowedComponents.length > 0
        ? this.allowedComponents
        : ['multi-checkbox', 'radiobutton', 'file-upload-manager', 'required-document-upload', 'textarea', 'date', 'fill-in', 'text-input', 'number-input', 'select'];

      return this.availableComponents.filter(component =>
        allowedTypes.includes(component.type)
      );
    }
    return this.availableComponents;
  }

  // 載入初始配置的共用方法
  private loadInitialConfig(): void {
    if (this.initialConfig && this.initialConfig.length > 0) {
      // 檢查是否為新格式（包含階段配置）
      if (this.isCompleteConfig(this.initialConfig)) {
        this.loadCompleteConfig(this.initialConfig as any);
      } else {
        // 舊格式：直接為表單欄位陣列
        this.loadFormFieldsConfig(this.initialConfig);
      }
    } else {
      // 如果沒有配置，清空表單項目
      this.formItems = [];
      this.selectedItem = null;
      this.emitFormItemsChange();
    }
  }

  // 檢查是否為包含階段配置的完整配置格式
  private isCompleteConfig(config: any): boolean {
    return config && typeof config === 'object' &&
           (config.hasOwnProperty('formFields') || config.hasOwnProperty('stageConfig'));
  }

  // 載入完整配置（包含階段配置）
  private loadCompleteConfig(completeConfig: any): void {
    // 載入表單欄位
    if (completeConfig.formFields && Array.isArray(completeConfig.formFields)) {
      this.loadFormFieldsConfig(completeConfig.formFields);
    }

    // 載入階段配置
    if (completeConfig.stageConfig) {
      this.enableStageMode = completeConfig.stageConfig.enableStageMode || false;
      this.formStages = completeConfig.stageConfig.formStages || [];

      // 更新表單項目的階段資訊
      this.updateFormItemsStageInfo();
    }
  }

  // 載入表單欄位配置
  private loadFormFieldsConfig(fieldsConfig: FormFieldConfig[]): void {
    // 保存當前選中項目的ID，以便重新載入後恢復選中狀態
    const selectedItemId = this.selectedItem?.id;

    this.formItems = fieldsConfig.map((config, index) => ({
      ...config,
      id: config.id || this.generateId(), // 使用現有ID或生成新的
      selected: false
    }));

    this.emitFormItemsChange();

    // 嘗試恢復之前選中的項目
    if (selectedItemId) {
      const previouslySelectedItem = this.formItems.find(item => item.id === selectedItemId);
      if (previouslySelectedItem) {
        this.selectedItem = previouslySelectedItem;
        previouslySelectedItem.selected = true;
        console.log('Restored previously selected item:', this.selectedItem);
      } else {
        // 如果找不到之前選中的項目，清除選擇
        this.selectedItem = null;
      }
    } else {
      // 如果之前沒有選中項目，保持為null
      this.selectedItem = null;
    }

    console.log('Form builder loaded config:', this.formItems);
  }

  // 更新表單項目的階段資訊
  private updateFormItemsStageInfo(): void {
    this.formItems.forEach(item => {
      if (this.enableStageMode) {
        // 標記未分配階段的元件
        (item as any)._unassigned = !item.stageId;

        // 設定階段順序（用於排序顯示）
        if (item.stageId) {
          const stage = this.formStages.find(s => s.id === item.stageId);
          (item as any)._stageOrder = stage ? stage.order : 999;
        } else {
          (item as any)._stageOrder = 999; // 未分配的放最後
        }
      } else {
        (item as any)._unassigned = false;
        (item as any)._stageOrder = undefined;
      }
    });
  }

  // 生成唯一ID
  private generateId(): string {
    return `form_item_${this.idCounter++}`;
  }

  // 生成字段名稱和ID
  private generateFieldName(type: string): string {
    const existingCount = this.formItems.filter(item => item.type === type).length;
    const baseName = type.replace('-', '_');
    return `${baseName}_${existingCount + 1}`;
  }

  // 生成字段ID
  private generateFieldId(name: string): string {
    return name; // 使用與name相同的值，保持一致性
  }

  // 拖拉放置處理
  // 檢查是否已存在簽核流程元件
  private hasApprovalFlowComponent(): boolean {
    return this.formItems.some(item => item.type === 'approval-flow');
  }

  // 檢查特定元件類型是否可以新增
  private canAddComponent(componentType: string): boolean {
    if (componentType === 'approval-flow') {
      return !this.hasApprovalFlowComponent();
    }
    return true;
  }

  // 公開方法，供模板使用，檢查特定元件是否可以新增
  canAddComponentType(componentType: string): boolean {
    return this.canAddComponent(componentType);
  }

  onDrop(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      // 同一容器內重新排序
      moveItemInArray(this.formItems, event.previousIndex, event.currentIndex);
    } else {
      // 從元件庫拖拉到建構區域
      let componentDef: FormComponentDefinition | undefined;

      // 從拖拽容器的 data 中獲取元件定義
      if (event.previousContainer.data && event.previousContainer.data.length > 0) {
        componentDef = event.previousContainer.data[event.previousIndex];
      }

      if (!componentDef) {
        console.error('無法找到對應的元件定義', event.previousContainer.data, event.previousIndex);
        return;
      }

      // 檢查是否可以新增此類型的元件
      if (!this.canAddComponent(componentDef.type)) {
        if (componentDef.type === 'approval-flow') {
          alert(this.i18nService.translate('form.builder.approval_flow_limit_message'));
        }
        return; // 阻止新增
      }

      const fieldName = this.generateFieldName(componentDef.type);

      // 確保選項被正確深拷貝
      let options: any[] | undefined = undefined;
      if (componentDef.defaultConfig.options) {
        if (componentDef.type === 'tags') {
          // 對於 tags，直接複製字串陣列
          options = [...componentDef.defaultConfig.options];
        } else {
          // 對於其他類型，深拷貝物件陣列
          options = componentDef.defaultConfig.options.map(option => ({ ...option }));
        }
      }

      const newItem: FormBuilderItem = {
        ...componentDef.defaultConfig,
        id: this.generateFieldId(fieldName),
        name: fieldName,
        selected: false
      } as FormBuilderItem;

      // 明確設定選項，確保不被 spread 覆蓋
      if (options !== undefined) {
        newItem.options = options;
      }

      // 階段模式下自動設定階段歸屬
      if (this.enableStageMode && this.currentEditingStage) {
        newItem.stageId = this.currentEditingStage;
      }

      // 複製到建構區域
      const newItems = [...this.formItems];
      newItems.splice(event.currentIndex, 0, newItem);
      this.formItems = newItems;

      // 自動選擇新添加的元件，讓用戶可以立即看到和編輯其屬性
      this.selectItem(newItem);
      this.emitFormItemsChange();
    }

    this.emitConfigChange();
  }

  // 選擇元件
  selectItem(item: FormBuilderItem): void {
    // 取消之前的選擇
    this.formItems.forEach(i => i.selected = false);

    // 選擇新項目
    item.selected = true;
    this.selectedItem = item;
  }

  // 刪除元件
  deleteItem(item: FormBuilderItem): void {
    const index = this.formItems.findIndex(i => i.id === item.id);
    if (index > -1) {
      this.formItems.splice(index, 1);

      // 如果刪除的是選中項目，清除選擇
      if (this.selectedItem?.id === item.id) {
        this.selectedItem = null;
      }

      this.emitFormItemsChange();
      this.emitConfigChange();
    }
  }

  // 複製元件
  duplicateItem(item: FormBuilderItem): void {
    // 檢查是否可以複製此類型的元件
    if (!this.canAddComponent(item.type)) {
      if (item.type === 'approval-flow') {
        alert('每個表單最多只能包含一個簽核流程元件，無法複製。');
      }
      return; // 阻止複製
    }

    const index = this.formItems.findIndex(i => i.id === item.id);
    if (index > -1) {
      const fieldName = this.generateFieldName(item.type);
      const duplicatedItem: FormBuilderItem = {
        ...item,
        id: this.generateFieldId(fieldName),
        name: fieldName,
        selected: false
      };

      this.formItems.splice(index + 1, 0, duplicatedItem);
      this.emitFormItemsChange();
      this.emitConfigChange();
    }
  }

  // 更新選中元件的屬性（防抖版本）
  updateSelectedItem(): void {
    this.updateSubject.next();
  }

  // 實際執行更新的方法
  private performUpdate(): void {
    if (this.selectedItem) {
      // 找到對應的項目並更新
      const index = this.formItems.findIndex(i => i.id === this.selectedItem!.id);
      if (index > -1) {
        this.formItems[index] = { ...this.selectedItem };
        this.emitConfigChange();
      }
    }
  }

  // 添加選項（用於 select、radio、checkbox、tags）
  addOption(): void {
    if (this.selectedItem && ['select', 'radiobutton', 'multi-checkbox', 'tags'].includes(this.selectedItem.type)) {
      if (!this.selectedItem.options) {
        this.selectedItem.options = [];
      }

      if (this.selectedItem.type === 'tags') {
        // 對於 tags 元件，直接添加字串
        this.selectedItem.options.push('新標籤');
      } else {
        // 對於其他元件，添加 label/value 物件
        const newOption = this.selectedItem.type === 'multi-checkbox'
          ? { label: '新選項', value: `option_${this.selectedItem.options.length + 1}`, selected: false }
          : { label: '新選項', value: `option_${this.selectedItem.options.length + 1}` };

        this.selectedItem.options.push(newOption);
      }
      this.updateSelectedItem();
    }
  }

  // 刪除選項
  removeOption(index: number): void {
    if (this.selectedItem && this.selectedItem.options && index >= 0 && index < this.selectedItem.options.length) {
      this.selectedItem.options.splice(index, 1);
      this.updateSelectedItem();
    }
  }

  // 處理題目圖片上傳
  onQuestionImageUpload(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0] && this.selectedItem) {
      const file = input.files[0];

      // 檔案大小和格式驗證
      if (!this.validateImageFile(file)) {
        return;
      }

      // 使用 FileUploadService 上傳
      this.fileUploadService.uploadFile(file).subscribe({
        next: (response) => {
          if (response.type === 'success' && response.response) {
            (this.selectedItem as any).questionImageUrl = response.response.data.viewUrl;
            (this.selectedItem as any).questionImageBlobName = response.response.data.blobName;
            this.updateSelectedItem();
          }
        },
        error: (error) => {
          console.error('題目圖片上傳失敗:', error);
          alert('圖片上傳失敗，請稍後再試');
        }
      });

      input.value = '';
    }
  }

  // 移除題目圖片
  removeQuestionImage(): void {
    if (this.selectedItem) {
      const blobName = (this.selectedItem as any).questionImageBlobName;
      if (blobName) {
        this.fileUploadService.deleteFile(blobName).subscribe({
          next: () => {
            (this.selectedItem as any).questionImageUrl = undefined;
            (this.selectedItem as any).questionImageBlobName = undefined;
            (this.selectedItem as any).questionImageAltText = undefined;
            this.updateSelectedItem();
          },
          error: (error) => {
            console.error('刪除題目圖片失敗:', error);
            alert('刪除圖片失敗，請稍後再試');
          }
        });
      } else {
        (this.selectedItem as any).questionImageUrl = undefined;
        (this.selectedItem as any).questionImageAltText = undefined;
        this.updateSelectedItem();
      }
    }
  }

  // 圖片檔案驗證
  validateImageFile(file: File): boolean {
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!allowedTypes.includes(file.type)) {
      alert('請選擇有效的圖片格式（JPG、PNG、GIF、WEBP）');
      return false;
    }

    if (file.size > maxSize) {
      alert('圖片大小不能超過 10MB');
      return false;
    }

    return true;
  }

  // 取得題目圖片 URL
  getQuestionImageUrl(): string | undefined {
    return this.selectedItem ? (this.selectedItem as any).questionImageUrl : undefined;
  }

  // 取得題目圖片替代文字
  getQuestionImageAltText(): string | undefined {
    return this.selectedItem ? (this.selectedItem as any).questionImageAltText : undefined;
  }

  // 設定題目圖片替代文字
  setQuestionImageAltText(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).questionImageAltText = value;
    }
  }

  // 發出表單項目變更事件
  private emitFormItemsChange(): void {
    this.formItemsChange.emit([...this.formItems]);
  }

  // 發射配置變更事件
  private emitConfigChange(): void {
    const formFields: FormFieldConfig[] = this.formItems.map(item => ({
      type: item.type,
      label: item.label,
      id: item.id,
      name: item.name,
      placeholder: item.placeholder,
      options: item.options,
      required: item.required,
      content: (item as any).content, // 段落元件的內容
      textAlign: (item as any).textAlign, // 段落元件的對齊方式
      format: (item as any).format, // 時間戳記元件的格式
      multiSelect: (item as any).multiSelect, // 人員選擇器的多選模式
      approvalFlowId: (item as any).approvalFlowId, // 簽核流程ID
      conditionalInputs: (item as any).conditionalInputs, // radiobutton 條件式輸入配置
      questionImageUrl: (item as any).questionImageUrl, // 題目圖片 URL
      questionImageBlobName: (item as any).questionImageBlobName, // Azure Blob 名稱
      questionImageAltText: (item as any).questionImageAltText, // 圖片替代文字
      files: (item as any).files, // 檔案顯示元件的檔案資料
      fileUploadConfig: (item as any).fileUploadConfig, // 檔案上傳管理器配置
      // 動態API下拉選單的屬性
      apiKey: (item as any).apiKey, // API Key
      // 子表單關聯的屬性
      formId: (item as any).formId, // 子表單ID
      // 自動編號元件的屬性
      autoNumbering: (item as any).autoNumbering, // 自動編號配置
      // 影片播放器元件的屬性
      youtubeUrl: (item as any).youtubeUrl,
      videoTitle: (item as any).videoTitle,
      videoDescription: (item as any).videoDescription,
      videoWidth: (item as any).videoWidth,
      videoHeight: (item as any).videoHeight,
      videoAutoplay: (item as any).videoAutoplay,
      videoControls: (item as any).videoControls,
      videoAllowFullscreen: (item as any).videoAllowFullscreen,
      // 指定證件上傳元件的屬性
      requiredDocumentConfig: (item as any).requiredDocumentConfig, // 證件上傳配置
      // 多人簽名元件的屬性
      title: (item as any).title, // 簽名標題
      documentId: (item as any).documentId, // 文件ID
      participants: (item as any).participants, // 參與者清單
      // 填空元件的屬性
      template: (item as any).template, // 填空模板
      fields: (item as any).fields, // 填空格配置
      // 階段化表單的屬性
      stageId: item.stageId // 階段歸屬
    }));



    // 如果啟用階段模式，加入階段配置
    if (this.enableStageMode) {
      (formFields as any).formStages = this.formStages;
    }

    // 為了保持向下相容性，同時發射兩種格式
    this.configChange.emit(formFields);
    this.previewChange.emit(formFields); // 預覽仍使用舊格式


  }

  // 保存階段配置
  private saveStageConfiguration(): void {
    const stageConfig = {
      enableStageMode: this.enableStageMode,
      formStages: this.formStages,
      lastUpdated: new Date().toISOString()
    };

    // 暫時保存到 localStorage，實際應該保存到後端
    if (this.formName) {
      localStorage.setItem(`stageConfig_${this.formName}`, JSON.stringify(stageConfig));
    }
  }

  // 載入階段配置
  private loadStageConfiguration(): void {
    if (this.formName) {
      const savedConfig = localStorage.getItem(`stageConfig_${this.formName}`);
      if (savedConfig) {
        try {
          const stageConfig = JSON.parse(savedConfig);
          this.enableStageMode = stageConfig.enableStageMode || false;
          this.formStages = stageConfig.formStages || [];
        } catch (error) {
          console.error('載入階段配置失敗:', error);
        }
      }
    }
  }

  // 清空表單
  clearForm(): void {
    this.formItems = [];
    this.selectedItem = null;
    this.emitFormItemsChange();
    this.emitConfigChange();
  }

  // 輔助方法：取得段落元件的內容
  getParagraphContent(item: FormBuilderItem): string {
    return (item as any).content || '段落內容';
  }

  // 輔助方法：取得段落元件的對齊方式
  getTextAlign(item: FormBuilderItem): string {
    return (item as any).textAlign || 'left';
  }

  // 輔助方法：取得時間戳記格式
  getTimestampFormat(item: FormBuilderItem): string {
    return (item as any).format || 'datetime';
  }

  // 輔助方法：取得人員選擇器的多選模式
  getMultiSelect(item: FormBuilderItem): boolean {
    return (item as any).multiSelect || false;
  }

  // 輔助方法：設定段落元件的內容
  setParagraphContent(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).content = value;
    }
  }

  // 輔助方法：設定段落元件的對齊方式
  setTextAlign(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).textAlign = value;
    }
  }

  // 輔助方法：設定時間戳記格式
  setTimestampFormat(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).format = value;
    }
  }

  // 輔助方法：設定人員選擇器的多選模式
  setMultiSelect(value: boolean): void {
    if (this.selectedItem) {
      (this.selectedItem as any).multiSelect = value;
    }
  }

  // 輔助方法：錄音元件屬性
  getMaxDuration(item: FormBuilderItem): number {
    return (item as any).maxDuration || 300;
  }

  setMaxDuration(value: number): void {
    if (this.selectedItem) {
      (this.selectedItem as any).maxDuration = value;
    }
  }

  getMinDuration(item: FormBuilderItem): number {
    return (item as any).minDuration || 1;
  }

  setMinDuration(value: number): void {
    if (this.selectedItem) {
      (this.selectedItem as any).minDuration = value;
    }
  }

  getAudioQuality(item: FormBuilderItem): string {
    return (item as any).audioQuality || 'medium';
  }

  setAudioQuality(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).audioQuality = value;
    }
  }

  getAudioFormat(item: FormBuilderItem): string {
    return (item as any).audioFormat || 'wav';
  }

  setAudioFormat(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).audioFormat = value;
    }
  }

  // Radiobutton 條件式輸入配置相關方法
  getConditionalInputsConfig(): any {
    if (!this.selectedItem) return null;
    return (this.selectedItem as any).conditionalInputs || {
      triggerValues: [],
      enableRemarks: false,
      enableFileUpload: false,
      enableAudioRecorder: false,
      remarksLabel: '',
      fileUploadLabel: '',
      audioRecorderLabel: ''
    };
  }

  setConditionalInputsConfig(config: any): void {
    if (this.selectedItem) {
      (this.selectedItem as any).conditionalInputs = config;
      this.updateSelectedItem();
    }
  }

  // 觸發值相關方法
  getConditionalTriggerValues(): string[] {
    const config = this.getConditionalInputsConfig();
    return config.triggerValues || [];
  }

  addConditionalTriggerValue(): void {
    const config = this.getConditionalInputsConfig();
    if (!config.triggerValues) {
      config.triggerValues = [];
    }
    config.triggerValues.push('');
    this.setConditionalInputsConfig(config);
  }

  removeConditionalTriggerValue(index: number): void {
    const config = this.getConditionalInputsConfig();
    if (config.triggerValues && index >= 0 && index < config.triggerValues.length) {
      config.triggerValues.splice(index, 1);
      this.setConditionalInputsConfig(config);
    }
  }

  updateConditionalTriggerValue(index: number, value: string): void {
    const config = this.getConditionalInputsConfig();
    if (config.triggerValues && index >= 0 && index < config.triggerValues.length) {
      config.triggerValues[index] = value;
      this.setConditionalInputsConfig(config);
    }
  }

  // 功能啟用相關方法
  getConditionalEnableRemarks(): boolean {
    const config = this.getConditionalInputsConfig();
    return config.enableRemarks || false;
  }

  setConditionalEnableRemarks(value: boolean): void {
    const config = this.getConditionalInputsConfig();
    config.enableRemarks = value;
    this.setConditionalInputsConfig(config);
  }

  getConditionalEnableFileUpload(): boolean {
    const config = this.getConditionalInputsConfig();
    return config.enableFileUpload || false;
  }

  setConditionalEnableFileUpload(value: boolean): void {
    const config = this.getConditionalInputsConfig();
    config.enableFileUpload = value;
    this.setConditionalInputsConfig(config);
  }

  getConditionalEnableAudioRecorder(): boolean {
    const config = this.getConditionalInputsConfig();
    return config.enableAudioRecorder || false;
  }

  setConditionalEnableAudioRecorder(value: boolean): void {
    const config = this.getConditionalInputsConfig();
    config.enableAudioRecorder = value;
    this.setConditionalInputsConfig(config);
  }

  // 標籤文字相關方法
  getConditionalRemarksLabel(): string {
    const config = this.getConditionalInputsConfig();
    return config.remarksLabel || '';
  }

  setConditionalRemarksLabel(value: string): void {
    const config = this.getConditionalInputsConfig();
    config.remarksLabel = value;
    this.setConditionalInputsConfig(config);
  }

  getConditionalFileUploadLabel(): string {
    const config = this.getConditionalInputsConfig();
    return config.fileUploadLabel || '';
  }

  setConditionalFileUploadLabel(value: string): void {
    const config = this.getConditionalInputsConfig();
    config.fileUploadLabel = value;
    this.setConditionalInputsConfig(config);
  }

  getConditionalAudioRecorderLabel(): string {
    const config = this.getConditionalInputsConfig();
    return config.audioRecorderLabel || '';
  }

  setConditionalAudioRecorderLabel(value: string): void {
    const config = this.getConditionalInputsConfig();
    config.audioRecorderLabel = value;
    this.setConditionalInputsConfig(config);
  }
  // 檔案上傳管理器輔助方法
  getFileUploadMaxFiles(item: FormBuilderItem): number {
    return (item as any).fileUploadConfig?.maxFiles || 5;
  }

  setFileUploadMaxFiles(value: number): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.maxFiles = value;
    }
  }

  getFileUploadMaxSize(item: FormBuilderItem): number {
    return (item as any).fileUploadConfig?.maxFileSize || 10;
  }

  setFileUploadMaxSize(value: number): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.maxFileSize = value;
    }
  }

  getFileUploadAllowedTypes(item: FormBuilderItem): string {
    const types = (item as any).fileUploadConfig?.allowedTypes || ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
    return types.join(',');
  }

  setFileUploadAllowedTypes(value: string): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.allowedTypes = value.split(',').map(t => t.trim()).filter(t => t);
    }
  }

  getFileUploadDragDrop(item: FormBuilderItem): boolean {
    return (item as any).fileUploadConfig?.enableDragDrop !== false;
  }

  setFileUploadDragDrop(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.enableDragDrop = value;
    }
  }

  getFileUploadPreview(item: FormBuilderItem): boolean {
    return (item as any).fileUploadConfig?.enablePreview !== false;
  }

  setFileUploadPreview(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.enablePreview = value;
    }
  }

  getFileUploadRename(item: FormBuilderItem): boolean {
    return (item as any).fileUploadConfig?.allowRename !== false;
  }

  setFileUploadRename(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.allowRename = value;
    }
  }

  getFileUploadAllowUserUpload(item: FormBuilderItem): boolean {
    return (item as any).fileUploadConfig?.allowUserUpload !== false;
  }

  setFileUploadAllowUserUpload(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).fileUploadConfig) {
        (this.selectedItem as any).fileUploadConfig = {};
      }
      (this.selectedItem as any).fileUploadConfig.allowUserUpload = value;
    }
  }

  // Required Document Upload 配置方法
  getRequiredDocumentName(item: FormBuilderItem): string {
    const config = (item as any).requiredDocumentConfig;
    if (config?.documentName) {
      return config.documentName;
    }
    return this.i18nService.translate('form.builder.default_document_name');
  }

  setRequiredDocumentName(value: string): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.documentName = value;
    }
  }

  getRequiredDocumentDescription(item: FormBuilderItem): string {
    return (item as any).requiredDocumentConfig?.documentDescription || '';
  }

  setRequiredDocumentDescription(value: string): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.documentDescription = value;
    }
  }

  getRequiredDocumentExpiryField(item: FormBuilderItem): string {
    return (item as any).requiredDocumentConfig?.expiryDateField || '';
  }

  setRequiredDocumentExpiryField(value: string): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.expiryDateField = value;
    }
  }

  getRequiredDocumentMaxSize(item: FormBuilderItem): number {
    return (item as any).requiredDocumentConfig?.maxFileSize || 10;
  }

  setRequiredDocumentMaxSize(value: number): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.maxFileSize = value;
    }
  }

  getRequiredDocumentAllowedTypes(item: FormBuilderItem): string {
    const types = (item as any).requiredDocumentConfig?.acceptedFormats;
    return Array.isArray(types) ? types.join(', ') : '.pdf, .jpg, .jpeg, .png';
  }
  public getDocumentName(item: FormBuilderItem): string {
    return item.requiredDocumentConfig?.documentName || this.i18nService.translate('form.builder.default_document_name');
  }

  public getAiParsingEnabled(item: FormBuilderItem): boolean {
    return !!item.requiredDocumentConfig?.aiParsing;
  }

  setRequiredDocumentAllowedTypes(value: string): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      const types = value.split(',').map(type => type.trim()).filter(type => type);
      (this.selectedItem as any).requiredDocumentConfig.acceptedFormats = types;
    }
  }

  getRequiredDocumentAIParsing(item: FormBuilderItem): boolean {
    return (item as any).requiredDocumentConfig?.aiParsing || false;
  }

  setRequiredDocumentAIParsing(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.aiParsing = value;
    }
  }

  getRequiredDocumentDragDrop(item: FormBuilderItem): boolean {
    return (item as any).requiredDocumentConfig?.enableDragDrop !== false;
  }

  setRequiredDocumentDragDrop(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.enableDragDrop = value;
    }
  }

  getRequiredDocumentPreview(item: FormBuilderItem): boolean {
    return (item as any).requiredDocumentConfig?.enablePreview !== false;
  }

  setRequiredDocumentPreview(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.enablePreview = value;
    }
  }

  getRequiredDocumentShowFileSize(item: FormBuilderItem): boolean {
    return (item as any).requiredDocumentConfig?.showFileSize !== false;
  }

  setRequiredDocumentShowFileSize(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.showFileSize = value;
    }
  }

  getRequiredDocumentShowUploadTime(item: FormBuilderItem): boolean {
    return (item as any).requiredDocumentConfig?.showUploadTime !== false;
  }

  setRequiredDocumentShowUploadTime(value: boolean): void {
    if (this.selectedItem) {
      if (!(this.selectedItem as any).requiredDocumentConfig) {
        (this.selectedItem as any).requiredDocumentConfig = {};
      }
      (this.selectedItem as any).requiredDocumentConfig.showUploadTime = value;
    }
  }

  // 安全的選項操作方法
  getOptionValue(index: number): string {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return '';
    }

    // 針對 tags 類型，直接返回字串值
    if (this.selectedItem.type === 'tags') {
      return this.selectedItem.options[index] || '';
    }

    // 針對其他類型，返回物件的 label
    const option = this.selectedItem.options[index];
    return option?.label || '';
  }

  getOptionName(index: number): string {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return '';
    }
    const option = this.selectedItem.options[index];
    return option?.label || '';
  }

  getOptionValueField(index: number): string {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return '';
    }
    const option = this.selectedItem.options[index];
    return option?.value || '';
  }

  updateOptionValue(index: number, value: string): void {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return;
    }

    // 針對 tags 類型，直接設定字串值
    if (this.selectedItem.type === 'tags') {
      this.selectedItem.options[index] = value;
    } else {
      // 針對其他類型，確保選項物件存在
      if (!this.selectedItem.options[index]) {
        this.selectedItem.options[index] = { label: '', value: '' };
      }
      this.selectedItem.options[index].label = value;
    }
    this.updateSelectedItem();
  }

  updateOptionName(index: number, value: string): void {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return;
    }

    if (!this.selectedItem.options[index]) {
      this.selectedItem.options[index] = { label: '', value: '' };
    }
    this.selectedItem.options[index].label = value;
    this.updateSelectedItem();
  }

  updateOptionValueField(index: number, value: string): void {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return;
    }

    if (!this.selectedItem.options[index]) {
      this.selectedItem.options[index] = { label: '', value: '' };
    }
    this.selectedItem.options[index].value = value;
    this.updateSelectedItem();
  }

  // 類型安全的事件處理方法
  onOptionValueInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value !== undefined) {
      this.updateOptionValue(index, target.value);
    }
  }

  onOptionValueBlur(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value !== undefined) {
      this.updateOptionValue(index, target.value);
    }
  }

  onOptionNameInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value !== undefined) {
      this.updateOptionName(index, target.value);
    }
  }

  onOptionNameBlur(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value !== undefined) {
      this.updateOptionName(index, target.value);
    }
  }

  onOptionValueFieldInput(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value !== undefined) {
      this.updateOptionValueField(index, target.value);
    }
  }

  onOptionValueFieldBlur(index: number, event: Event): void {
    const target = event.target as HTMLInputElement;
    if (target?.value !== undefined) {
      this.updateOptionValueField(index, target.value);
    }
  }

  // 簽核流程相關方法
  loadApprovalFlows(): void {
    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      console.warn('缺少必要的公司 ID 或助理編號，無法載入簽核流程');
      return;
    }

    this.approvalService.getApprovalFlowDefinitions(companyid, assistantNo).subscribe({
      next: (response) => {
        if (response && response.success && response.data) {
          this.availableApprovalFlows = response.data.filter((flow: ApprovalFlowDefinition) => flow.status === 'active');
          console.log('Form Builder - 載入簽核流程成功:', this.availableApprovalFlows);
        } else {
          this.availableApprovalFlows = [];
          console.warn('Form Builder - API 回應格式不正確或無資料');
        }
      },
      error: (err: any) => {
        console.error('Form Builder - 載入簽核流程失敗:', err);
        this.availableApprovalFlows = [];
      }
    });
  }

  getSelectedApprovalFlowId(item: FormBuilderItem): string {
    return (item as any).approvalFlowId || '';
  }

  setApprovalFlow(flowId: string): void {
    if (this.selectedItem) {
      // 直接更新選中項目的屬性
      (this.selectedItem as any).approvalFlowId = flowId;

      // 直接更新對應的 formItems，確保數據同步
      const index = this.formItems.findIndex(i => i.id === this.selectedItem!.id);
      if (index > -1) {
        // 更新 formItems 中的項目，保持對象引用一致
        this.formItems[index] = { ...this.selectedItem };
        // 同時更新 selectedItem 的引用，確保視圖綁定正確
        this.selectedItem = this.formItems[index];
        this.selectedItem.selected = true;

        // 直接發射配置變更，避免經過防抖機制
        this.emitConfigChange();
      }
    }
  }

  getSelectedApprovalFlow(item: FormBuilderItem): ApprovalFlowDefinition | null {
    const flowId = (item as any).approvalFlowId;
    if (!flowId) return null;
    return this.availableApprovalFlows.find(flow => flow.id === flowId) || null;
  }

  getSelectedFlow(item: FormBuilderItem): ApprovalFlowDefinition | null {
    return this.getSelectedApprovalFlow(item);
  }

  // 填空元件相關方法
  getFillInFields(item: FormBuilderItem): any[] {
    return (item as any).fields || [];
  }

  addFillInField(): void {
    if (this.selectedItem && this.selectedItem.type === 'fill-in') {
      if (!(this.selectedItem as any).fields) {
        (this.selectedItem as any).fields = [];
      }
      (this.selectedItem as any).fields.push({
        id: 'field' + (this.getFillInFields(this.selectedItem).length + 1),
        type: 'text',
        width: 'md',
        placeholder: ''
      });
      this.updateSelectedItem();
    }
  }

  removeFillInField(index: number): void {
    if (this.selectedItem && this.selectedItem.type === 'fill-in') {
      const fields = this.getFillInFields(this.selectedItem);
      if (fields && index >= 0 && index < fields.length) {
        fields.splice(index, 1);
        this.updateSelectedItem();
      }
    }
  }

  // 搜尋和過濾相關方法
  updateFilteredComponents(): void {
    const baseComponents = this.availableComponentsFiltered; // 使用過濾後的元件
    if (!this.searchTerm.trim()) {
      this.filteredComponents = [...baseComponents];
    } else {
      const searchLower = this.searchTerm.toLowerCase();
      this.filteredComponents = baseComponents.filter(component =>
        component.label.toLowerCase().includes(searchLower) ||
        component.type.toLowerCase().includes(searchLower)
      );
    }
  }

  onSearchChange(): void {
    this.updateFilteredComponents();
  }

  clearSearch(): void {
    this.searchTerm = '';
    this.updateFilteredComponents();
  }

  // 分類管理方法
  toggleCategory(categoryId: string): void {
    const category = this.categories.find(cat => cat.id === categoryId);
    if (category) {
      category.expanded = !category.expanded;
    }
  }

  getCategoryComponents(categoryId: string): FormComponentDefinition[] {
    const baseComponents = this.availableComponentsFiltered; // 使用過濾後的元件
    if (this.searchTerm.trim()) {
      // 搜尋模式：返回該分類下的過濾結果
      return this.filteredComponents.filter(component => component.category === categoryId);
    } else {
      // 正常模式：返回該分類下的所有元件
      return baseComponents.filter(component => component.category === categoryId);
    }
  }

  hasVisibleComponents(categoryId: string): boolean {
    return this.getCategoryComponents(categoryId).length > 0;
  }

  // 切換面板收合狀態
  togglePanelCollapse(panelName: keyof typeof this.panelCollapsedState): void {
    this.panelCollapsedState[panelName] = !this.panelCollapsedState[panelName];
  }

  // 檢查面板是否收合
  isPanelCollapsed(panelName: keyof typeof this.panelCollapsedState): boolean {
    return this.panelCollapsedState[panelName];
  }

  // 多人簽名元件屬性方法
  getSignatureTitle(item: FormBuilderItem): string {
    return (item as any).title || '多人簽名';
  }

  setSignatureTitle(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).title = value;
    }
  }

  getDocumentId(item: FormBuilderItem): string {
    return (item as any).documentId || '';
  }

  setDocumentId(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).documentId = value;
    }
  }

  // 富文本顯示元件的輔助方法
  getRichTextContent(item: FormBuilderItem): string {
    return (item as any).content || '<p class="placeholder-text">請輸入富文本內容</p>';
  }

  setRichTextContent(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).content = value;
    }
  }

  getRichTextFontSize(item: FormBuilderItem): string {
    return (item as any).fontSize || '14px';
  }

  setRichTextFontSize(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).fontSize = value;
    }
  }

  getRichTextColor(item: FormBuilderItem): string {
    return (item as any).color || '#333333';
  }

  setRichTextColor(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).color = value;
    }
  }

  getRichTextTextAlign(item: FormBuilderItem): string {
    return (item as any).textAlign || 'left';
  }

  setRichTextTextAlign(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).textAlign = value;
    }
  }

  getRichTextLineHeight(item: FormBuilderItem): string {
    return (item as any).lineHeight || '1.5';
  }

  setRichTextLineHeight(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).lineHeight = value;
    }
  }

  getRichTextBackgroundColor(item: FormBuilderItem): string {
    return (item as any).backgroundColor || '';
  }

  setRichTextBackgroundColor(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).backgroundColor = value;
    }
  }

  getRichTextPadding(item: FormBuilderItem): string {
    return (item as any).padding || '';
  }

  setRichTextPadding(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).padding = value;
    }
  }

  getRichTextBorderRadius(item: FormBuilderItem): string {
    return (item as any).borderRadius || '';
  }

  setRichTextBorderRadius(value: string): void {
    if (this.selectedItem) {
      (this.selectedItem as any).borderRadius = value;
    }
  }

  // 多選核取方塊條件式文字輸入相關方法
  getOptionEnableTextInput(index: number): boolean {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return false;
    }
    const option = this.selectedItem.options[index] as any;
    return option.enableTextInput || false;
  }

  setOptionEnableTextInput(index: number, value: boolean): void {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return;
    }
    const option = this.selectedItem.options[index] as any;
    option.enableTextInput = value;

    // 如果禁用文字輸入，清除相關設定
    if (!value) {
      delete option.textPlaceholder;
      delete option.textRequired;
    } else {
      // 如果啟用文字輸入，設定預設值
      if (!option.textPlaceholder) {
        option.textPlaceholder = '請輸入...';
      }
      if (option.textRequired === undefined) {
        option.textRequired = false;
      }
    }

    this.updateSelectedItem();
  }

  getOptionTextPlaceholder(index: number): string {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return '';
    }
    const option = this.selectedItem.options[index] as any;
    return option.textPlaceholder || '請輸入...';
  }

  setOptionTextPlaceholder(index: number, value: string): void {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return;
    }
    const option = this.selectedItem.options[index] as any;
    option.textPlaceholder = value;
    this.updateSelectedItem();
  }

  getOptionTextRequired(index: number): boolean {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return false;
    }
    const option = this.selectedItem.options[index] as any;
    return option.textRequired || false;
  }

  setOptionTextRequired(index: number, value: boolean): void {
    if (!this.selectedItem?.options || index >= this.selectedItem.options.length) {
      return;
    }
    const option = this.selectedItem.options[index] as any;
    option.textRequired = value;
    this.updateSelectedItem();
  }

  // ===== 階段管理方法 =====

  onStageModeToggle(): void {
    if (this.enableStageMode) {
      this.initializeDefaultStages();
      this.showStageGuideIfNeeded();
    } else {
      this.clearStageSettings();
    }
  }

  initializeDefaultStages(): void {
    if (this.formStages.length === 0) {
      this.formStages = [
        {
          id: this.generateStageId(),
          name: '階段一',
          description: '',
          order: 1,
          requiredDepartments: [],
          allowedUsers: [],
          fields: [],
          status: 'pending'
        }
      ];
    }
  }

  clearStageSettings(): void {
    // 移除所有元件的階段歸屬
    this.formItems.forEach(item => {
      delete item.stageId;
    });
    if (confirm('確定要清除所有階段設定嗎？')) {
      this.formStages = [];
      this.currentEditingStage = null;
    }
  }

  selectStage(stageId: string | null): void {
    this.currentEditingStage = stageId;
  }

  getFilteredFormItems(): FormBuilderItem[] {
    if (!this.enableStageMode || this.currentEditingStage === null) {
      // 全部檢視模式：按階段順序排列
      return this.getSortedFormItemsByStage();
    }
    return this.formItems.filter(item =>
      item.stageId === this.currentEditingStage || !item.stageId
    );
  }

  // 按階段順序排列表單元件
  getSortedFormItemsByStage(): FormBuilderItem[] {
    const sortedItems: FormBuilderItem[] = [];

    // 首先添加未分配階段的元件
    const unassignedItems = this.formItems.filter(item => !item.stageId);
    if (unassignedItems.length > 0) {
      // 為每個未分配元件添加特殊標記
      unassignedItems.forEach(item => {
        item._unassigned = true;
      });
      sortedItems.push(...unassignedItems);
    }

    // 然後按照階段順序添加每個階段的元件
    this.getSortedStages().forEach(stage => {
      const stageItems = this.formItems.filter(item => item.stageId === stage.id);
      if (stageItems.length > 0) {
        // 為每個階段元件添加階段順序標記
        stageItems.forEach(item => {
          item._stageOrder = stage.order;
          item._unassigned = false;
        });
        sortedItems.push(...stageItems);
      }
    });

    return sortedItems;
  }

  addNewStage(): void {
    this.editingStage = {
      id: this.generateStageId(),
      name: `階段${this.formStages.length + 1}`,
      description: '',
      order: this.formStages.length + 1,
      requiredDepartments: [],
      allowedUsers: [],
      fields: [],
      status: 'pending'
    };
    this.openStageEditor();
  }

  editStage(stage: FormStage): void {
    this.editingStage = { ...stage };
    this.openStageEditor();
  }

  deleteStage(stage: FormStage): void {
    if (confirm(`確定要刪除階段「${stage.name}」嗎？`)) {
      this.formStages = this.formStages.filter(s => s.id !== stage.id);

      // 移除相關元件的階段歸屬
      this.formItems.forEach(item => {
        if (item.stageId === stage.id) {
          delete item.stageId;
        }
      });

      // 如果刪除的是當前編輯的階段，切換到全部檢視
      if (this.currentEditingStage === stage.id) {
        this.currentEditingStage = null;
      }

      // 如果刪除後沒有階段了，關閉多階段模式
      if (this.formStages.length === 0) {
        this.enableStageMode = false;
        this.onStageModeToggle();
      }

      this.emitConfigChange();
    }
  }

  openStageEditor(): void {
    // Stage editor is now controlled by the showStageEditor property
    this.showStageEditor = true;
  }

  closeStageEditor(): void {
    this.showStageEditor = false;
    this.editingStage = null;
  }

  saveStage(stage: FormStage): void {
    if (stage.id && this.formStages.find(s => s.id === stage.id)) {
      // 編輯現有階段
      const index = this.formStages.findIndex(s => s.id === stage.id);
      this.formStages[index] = stage;
    } else {
      // 新增階段
      if (!stage.id) {
        stage.id = this.generateStageId();
      }
      // 確保新階段有正確的順序
      if (!stage.order) {
        stage.order = this.formStages.length + 1;
      }
      this.formStages.push(stage);
    }
    this.closeStageEditor();
    this.emitConfigChange();
  }

  generateStageId(): string {
    return 'stage_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  getCurrentStageName(): string {
    const stage = this.formStages.find(s => s.id === this.currentEditingStage);
    return stage ? stage.name : '';
  }

  getStageName(stageId: string): string {
    const stage = this.formStages.find(s => s.id === stageId);
    return stage ? stage.name : '';
  }

  getStageOrder(stage: FormStage): number {
    return stage.order;
  }

  getStageFieldsCount(stageId: string): number {
    return this.formItems.filter(item => item.stageId === stageId).length;
  }

  isItemInDifferentStage(item: FormBuilderItem): boolean {
    if (!this.enableStageMode || this.currentEditingStage === null) {
      return false;
    }
    return item.stageId !== this.currentEditingStage && !!item.stageId;
  }

  showStageGuideIfNeeded(): void {
    // TODO: 實作首次使用引導
    this.isFirstTimeStageUser = true;
  }

  startStageWizard(): void {
    this.showStageWizard = true;
    this.showStageTemplatesDialog();
  }

  showStageTemplatesDialog(): void {
    const templates = this.getStageTemplates();
    const templateNames = templates.map(t => t.name).join('\n');

    if (confirm(`選擇常用的階段範本：\n\n${templateNames}\n\n按確定選擇第一個範本，按取消手動設定`)) {
      this.applyStageTemplate(templates[0]);
    }
  }

  getStageTemplates(): Array<{name: string, stages: Omit<FormStage, 'id'>[]}> {
    return [
      {
        name: '工程申請表單 (申請→安全→工程→現場)',
        stages: [
          {
            name: '申請人填寫',
            description: '申請人填寫基本資訊和工程需求',
            order: 1,
            requiredDepartments: ['申請部門'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          },
          {
            name: '安全部門評估',
            description: '安全部門進行風險評估和安全檢查',
            order: 2,
            requiredDepartments: ['安全部'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          },
          {
            name: '工程部門確認',
            description: '工程部門確認技術可行性和資源分配',
            order: 3,
            requiredDepartments: ['工程部'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          },
          {
            name: '現場負責人執行',
            description: '現場負責人執行確認和施工管理',
            order: 4,
            requiredDepartments: ['現場管理'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          }
        ]
      },
      {
        name: '採購申請表單 (申請→財務→採購)',
        stages: [
          {
            name: '申請人填寫',
            description: '填寫採購需求和預算資訊',
            order: 1,
            requiredDepartments: ['申請部門'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          },
          {
            name: '財務部門審核',
            description: '財務部門進行預算審核和成本評估',
            order: 2,
            requiredDepartments: ['財務部'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          },
          {
            name: '採購部門執行',
            description: '採購部門執行採購流程和供應商管理',
            order: 3,
            requiredDepartments: ['採購部'],
            allowedUsers: [],
            fields: [],
            status: 'pending' as const
          }
        ]
      }
    ];
  }

  applyStageTemplate(template: {name: string, stages: Omit<FormStage, 'id'>[]}): void {
    this.formStages = template.stages.map(stage => ({
      ...stage,
      id: this.generateStageId()
    }));
    this.showStageWizard = false;
    this.isFirstTimeStageUser = false;
  }

  dismissGuide(): void {
    this.isFirstTimeStageUser = false;
  }

  // 自動分配欄位到階段
  autoAssignFieldsToStages(): void {
    const unassignedFields = this.formItems.filter(item => !item.stageId);

    if (unassignedFields.length === 0 || this.formStages.length === 0) {
      return;
    }

    const fieldsPerStage = Math.ceil(unassignedFields.length / this.formStages.length);

    unassignedFields.forEach((field, index) => {
      const stageIndex = Math.floor(index / fieldsPerStage);
      if (stageIndex < this.formStages.length) {
        field.stageId = this.formStages[stageIndex].id;
      }
    });

    this.emitConfigChange();
  }

  // 批次操作：移動所有選中階段的欄位到另一階段
  moveStageFields(fromStageId: string, toStageId: string): void {
    this.formItems.forEach(item => {
      if (item.stageId === fromStageId) {
        item.stageId = toStageId;
      }
    });
    this.emitConfigChange();
  }

  // 獲取表單完成度統計
  getFormCompletionStats(): {total: number, assigned: number, unassigned: number} {
    const total = this.formItems.length;
    const assigned = this.formItems.filter(item => item.stageId).length;
    const unassigned = total - assigned;

    return { total, assigned, unassigned };
  }

  // 驗證階段設定是否完整
  validateStageSetup(): {valid: boolean, issues: string[]} {
    const issues: string[] = [];

    if (this.enableStageMode) {
      if (this.formStages.length === 0) {
        issues.push('至少需要建立一個階段');
      }

      // 檢查是否有空階段
      const emptyStages = this.formStages.filter(stage =>
        this.getStageFieldsCount(stage.id) === 0
      );
      if (emptyStages.length > 0) {
        issues.push(`階段「${emptyStages.map(s => s.name).join('、')}」沒有分配任何欄位`);
      }

      // 檢查是否有未分配的欄位
      const unassignedCount = this.getFormCompletionStats().unassigned;
      if (unassignedCount > 0) {
        issues.push(`有 ${unassignedCount} 個欄位尚未分配到階段`);
      }

      // 檢查階段是否都有負責部門
      const stagesWithoutDepts = this.formStages.filter(stage =>
        stage.requiredDepartments.length === 0
      );
      if (stagesWithoutDepts.length > 0) {
        issues.push(`階段「${stagesWithoutDepts.map(s => s.name).join('、')}」尚未設定負責部門`);
      }
    }

    return {
      valid: issues.length === 0,
      issues
    };
  }

  // 驗證所有階段設定
  validateAllStages(): void {
    const issues: string[] = [];

    // 檢查是否有階段沒有任何欄位
    this.formStages.forEach((stage, index) => {
      const fieldCount = this.getStageFieldsCount(stage.id);
      if (fieldCount === 0) {
        issues.push(`階段 ${index + 1} (${stage.name}) 沒有任何欄位`);
      }
    });

    // 檢查是否有未分配的欄位
    const unassignedCount = this.getFormCompletionStats().unassigned;
    if (unassignedCount > 0) {
      issues.push(`有 ${unassignedCount} 個欄位未分配到任何階段`);
    }

    // 檢查階段是否有重複名稱
    const stageNames = this.formStages.map(s => s.name);
    const duplicates = stageNames.filter((name, index) => stageNames.indexOf(name) !== index);
    if (duplicates.length > 0) {
      issues.push(`發現重複的階段名稱：${[...new Set(duplicates)].join(', ')}`);
    }

    // 顯示驗證結果
    if (issues.length === 0) {
      alert('✅ 所有階段設定都正確！');
    } else {
      alert('⚠️ 發現以下問題：\n\n' + issues.map((issue, i) => `${i + 1}. ${issue}`).join('\n'));
    }
  }

  // 取得按順序排列的階段
  getSortedStages(): FormStage[] {
    return this.formStages.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  // 檢查必需文檔是否啟用 AI 解析
  isRequiredDocumentAiParsingEnabled(item: FormBuilderItem): boolean {
    const config = (item as any).requiredDocumentConfig;
    return config?.aiParsing === true;
  }

  // 自動編號相關方法
  getAutoNumberingSeparator(item: FormBuilderItem): string {
    return (item as any).autoNumbering?.separator || '-';
  }

  setAutoNumberingSeparator(item: FormBuilderItem, separator: string): void {
    if (!item.autoNumbering) {
      (item as any).autoNumbering = { elements: [] };
    }
    (item as any).autoNumbering.separator = separator;
    this.updateSelectedItem();
  }

  getAutoNumberingElements(item: FormBuilderItem): any[] {
    return (item as any).autoNumbering?.elements || [];
  }

  addAutoNumberingElement(item: FormBuilderItem): void {
    if (!item.autoNumbering) {
      (item as any).autoNumbering = { elements: [] };
    }
    const elements = (item as any).autoNumbering.elements;
    const newElement = {
      type: 'text',
      order: elements.length + 1,
      config: { text: '' }
    };
    elements.push(newElement);
    this.updateSelectedItem();
  }

  removeAutoNumberingElement(item: FormBuilderItem, index: number): void {
    const elements = this.getAutoNumberingElements(item);
    if (elements && index >= 0 && index < elements.length) {
      elements.splice(index, 1);
      // 重新設定順序
      elements.forEach((element: any, i: number) => {
        element.order = i + 1;
      });
      this.updateSelectedItem();
    }
  }

  moveAutoNumberingElement(item: FormBuilderItem, index: number, direction: 'up' | 'down'): void {
    const elements = this.getAutoNumberingElements(item);
    if (!elements || elements.length < 2) return;

    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= elements.length) return;

    // 交換元素
    [elements[index], elements[newIndex]] = [elements[newIndex], elements[index]];

    // 重新設定順序
    elements.forEach((element: any, i: number) => {
      element.order = i + 1;
    });

    this.updateSelectedItem();
  }

  updateAutoNumberingElement(item: FormBuilderItem, index: number, property: string, value: any): void {
    const elements = this.getAutoNumberingElements(item);
    if (!elements || index < 0 || index >= elements.length) return;

    const element = elements[index];
    if (property === 'type') {
      element.type = value;
      // 根據類型設定預設配置
      switch (value) {
        case 'text':
          element.config = { text: '' };
          break;
        case 'dropdown':
          element.config = {
            label: '',
            placeholder: '請選擇',
            options: [{ label: '選項1', value: 'option1' }]
          };
          break;
        case 'year':
          element.config = { format: 'YYYY' };
          break;
        case 'month':
          element.config = { format: 'MM' };
          break;
        case 'sequence':
          element.config = { digits: 3, startFrom: 1, apiKey: '' };
          break;
      }
    }
    this.updateSelectedItem();
  }

  updateAutoNumberingElementConfig(item: FormBuilderItem, index: number): void {
    // 觸發更新以同步配置變更
    this.updateSelectedItem();
  }

  addDropdownOption(item: FormBuilderItem, elementIndex: number): void {
    const elements = this.getAutoNumberingElements(item);
    if (!elements || elementIndex < 0 || elementIndex >= elements.length) return;

    const element = elements[elementIndex];
    if (element.type === 'dropdown') {
      if (!element.config.options) {
        element.config.options = [];
      }
      const optionCount = element.config.options.length + 1;
      element.config.options.push({
        label: `選項${optionCount}`,
        value: `option${optionCount}`
      });
      this.updateSelectedItem();
    }
  }

  removeDropdownOption(item: FormBuilderItem, elementIndex: number, optionIndex: number): void {
    const elements = this.getAutoNumberingElements(item);
    if (!elements || elementIndex < 0 || elementIndex >= elements.length) return;

    const element = elements[elementIndex];
    if (element.type === 'dropdown' && element.config.options) {
      element.config.options.splice(optionIndex, 1);
      this.updateSelectedItem();
    }
  }

  generateAutoNumberingPreview(item: FormBuilderItem): string {
    const elements = this.getAutoNumberingElements(item);
    const separator = this.getAutoNumberingSeparator(item);

    if (!elements || elements.length === 0) {
      return 'DOC-2024-001';
    }

    const previewParts: string[] = [];
    const sortedElements = elements.sort((a: any, b: any) => a.order - b.order);

    for (const element of sortedElements) {
      let part = '';
      switch (element.type) {
        case 'text':
          part = element.config.text || '[文字]';
          break;
        case 'dropdown':
          const options = element.config.options || [];
          part = options.length > 0 ? options[0].label : '[選項]';
          break;
        case 'year':
          const year = new Date().getFullYear();
          part = element.config.format === 'YYYY' ? year.toString() : year.toString().slice(-2);
          break;
        case 'month':
          const month = new Date().getMonth() + 1;
          part = element.config.format === 'MM' ? month.toString().padStart(2, '0') : month.toString();
          break;
        case 'sequence':
          const digits = element.config.digits || 3;
          const startFrom = element.config.startFrom || 1;
          part = startFrom.toString().padStart(digits, '0');
          break;
        default:
          part = '[未知]';
      }
      previewParts.push(part);
    }

    return previewParts.join(separator);
  }

  updateAutoNumberingPreview(item: FormBuilderItem): void {
    const preview = this.generateAutoNumberingPreview(item);
    if (!item.autoNumbering) {
      (item as any).autoNumbering = { elements: [] };
    }
    (item as any).autoNumbering.preview = preview;
    this.updateSelectedItem();
  }
}
